import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { XCircleIcon, CheckIcon, ExclamationIcon, PaperAirplaneIcon, EyeIcon} from '@heroicons/react/24/outline';
import { crearGarantia, obtenerGarantías, actualizarEstadoGarantia, enviarWhatsAppGarantia } from '../api/backend_v2';
import { formatearMoneda } from '../utils/moneda';

const ModalGestionGarantia = ({ anteojo, cliente, isOpen, onClose, onGarantiaCreada }) => {
  const [tipoGarantia, setTipoGarantia] = useState('cristal');
  const [descripcionProducto, setDescripcionProducto] = useState('');
  const [numeroProveedor, setNumeroProveedor] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [enviarWhatsApp, setEnviarWhatsApp] = useState(true);
  const [garantias, setGarantias] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState('crear'); // 'crear' o 'historial'
  const [editandoGarantia, setEditandoGarantia] = useState(null);
  const [nuevoEstado, setNuevoEstado] = useState('');

  useEffect(() => {
    if (isOpen && anteojo?.id) {
      cargarGarantías();
      // Prellenar datos según el tipo de garantía seleccionado
      actualizarDescripcionProducto('cristal');
    }
  }, [isOpen, anteojo, tipoGarantia]);

  const cargarGarantías = async () => {
    try {
      const data = await obtenerGarantías(anteojo.id);
      setGarantias(data || []);
    } catch (error) {
      console.error('Error al cargar garantías:', error);
    }
  };

  const actualizarDescripcionProducto = (tipo) => {
    if (tipo === 'cristal') {
      const cristales = [anteojo?.lejos_cristal, anteojo?.cerca_cristal].filter(Boolean).join(' / ');
      setDescripcionProducto(cristales || 'Cristal');
    } else if (tipo === 'armazon') {
      const armazones = [anteojo?.lejos_armazon, anteojo?.cerca_armazon].filter(Boolean).join(' / ');
      setDescripcionProducto(armazones || 'Armazón');
    }
  };

  const handleCrearGarantia = async (e) => {
    e.preventDefault();
    
    if (!numeroProveedor && enviarWhatsApp) {
      Swal.fire('Error', 'Ingresa el número de WhatsApp del proveedor para enviar mensaje', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await crearGarantia({
        anteojo_id: anteojo.id,
        tipo_garantia: tipoGarantia,
        descripcion_producto: descripcionProducto,
        numero_proveedor: numeroProveedor || null,
        observaciones: observaciones,
        enviar_whatsapp: enviarWhatsApp && !!numeroProveedor
      });

      await Swal.fire('Éxito', '¡Garantía registrada exitosamente!', 'success');
      
      // Limpiar formulario
      setDescripcionProducto('');
      setNumeroProveedor('');
      setObservaciones('');
      setEnviarWhatsApp(true);
      
      // Recargar garantías
      await cargarGarantías();
      
      // Notificar al padre
      if (onGarantiaCreada) {
        onGarantiaCreada();
      }

      // Cambiar a pestaña de historial
      setTab('historial');
    } catch (error) {
      console.error('Error:', error);
      Swal.fire('Error', error.message || 'Error al crear garantía', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleActualizarEstado = async (garantia) => {
    try {
      const { value: nuevoEstadoSeleccionado } = await Swal.fire({
        title: 'Actualizar estado de garantía',
        input: 'select',
        inputOptions: {
          'tomada': 'Tomada',
          'reclamada': 'Reclamada',
          'aprobada': 'Aprobada',
          'no_aprobada': 'No aprobada',
          'finalizada': 'Finalizada'
        },
        inputValue: garantia.estado,
        showCancelButton: true,
        confirmButtonText: 'Actualizar',
        cancelButtonText: 'Cancelar'
      });

      if (nuevoEstadoSeleccionado) {
        await actualizarEstadoGarantia(garantia.id, {
          estado: nuevoEstadoSeleccionado,
          observaciones: garantia.observaciones
        });

        Swal.fire('Éxito', 'Estado actualizado', 'success');
        await cargarGarantías();
      }
    } catch (error) {
      Swal.fire('Error', error.message || 'Error al actualizar estado', 'error');
    }
  };

  const handleEnviarWhatsApp = async (garantia) => {
    try {
      if (!garantia.numero_proveedor) {
        Swal.fire('Error', 'No hay número de proveedor guardado', 'error');
        return;
      }

      const { value: mensaje } = await Swal.fire({
        title: 'Enviar mensaje WhatsApp',
        input: 'textarea',
        inputValue: `Hola, tenemos una reclamación de garantía:\n- Tipo: ${garantia.tipo_garantia}\n- Producto: ${garantia.descripcion_producto}\n- Observaciones: ${garantia.observaciones || 'Sin observaciones'}\n\nPor favor, contactarlos para coordinar la revisión.`,
        inputPlaceholder: 'Escribe el mensaje a enviar...',
        showCancelButton: true,
        confirmButtonText: 'Enviar',
        cancelButtonText: 'Cancelar'
      });

      if (mensaje !== undefined) {
        await enviarWhatsAppGarantia(garantia.id, {
          numero_proveedor: garantia.numero_proveedor,
          mensaje: mensaje
        });

        Swal.fire('Éxito', 'Mensaje enviado a WhatsApp', 'success');
        await cargarGarantías();
      }
    } catch (error) {
      Swal.fire('Error', error.message || 'Error al enviar WhatsApp', 'error');
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'tomada':
        return 'bg-blue-100 text-blue-800';
      case 'reclamada':
        return 'bg-yellow-100 text-yellow-800';
      case 'aprobada':
        return 'bg-green-100 text-green-800';
      case 'no_aprobada':
        return 'bg-red-100 text-red-800';
      case 'finalizada':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoLabel = (estado) => {
    const labels = {
      'tomada': 'Tomada',
      'reclamada': 'Reclamada',
      'aprobada': 'Aprobada',
      'no_aprobada': 'No aprobada',
      'finalizada': 'Finalizada'
    };
    return labels[estado?.toLowerCase()] || estado;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-20 inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Encabezado */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h2 className="text-2xl font-bold">Gestión de Garantía</h2>
            <p className="text-blue-100">Sobre #{anteojo?.nro_sobre} - {cliente?.nombre} {cliente?.apellido}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-700 p-2 rounded-full transition"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setTab('crear')}
            className={`flex-1 py-3 px-4 font-medium transition ${
              tab === 'crear'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Crear Garantía
          </button>
          <button
            onClick={() => setTab('historial')}
            className={`flex-1 py-3 px-4 font-medium transition relative ${
              tab === 'historial'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Historial de Garantías
            {garantias.length > 0 && (
              <span className="absolute top-1 right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                {garantias.length}
              </span>
            )}
          </button>
        </div>

        {/* Contenido */}
        <div className="p-6">
          {tab === 'crear' ? (
            // Formulario de crear garantía
            <form onSubmit={handleCrearGarantia} className="space-y-4">
              {/* Tipo de Garantía */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo de Garantía
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setTipoGarantia('cristal');
                      actualizarDescripcionProducto('cristal');
                    }}
                    className={`p-3 border-2 rounded-lg font-medium transition ${
                      tipoGarantia === 'cristal'
                        ? 'border-blue-600 bg-blue-50 text-blue-600'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <EyeIcon className="h-5 w-5 inline-block mr-2" /> Cristales
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTipoGarantia('armazon');
                      actualizarDescripcionProducto('armazon');
                    }}
                    className={`p-3 border-2 rounded-lg font-medium transition ${
                      tipoGarantia === 'armazon'
                        ? 'border-blue-600 bg-blue-50 text-blue-600'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    👓 Armazón
                  </button>
                </div>
              </div>

              {/* Fecha (solo lectura) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha de Garantía
                </label>
                <input
                  type="date"
                  value={new Date().toISOString().split('T')[0]}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>

              {/* Descripción del Producto */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Descripción del Producto
                </label>
                <input
                  type="text"
                  value={descripcionProducto}
                  onChange={(e) => setDescripcionProducto(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: Cristales multifocales, Armazón Rayban"
                />
              </div>

              {/* Observaciones */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Observaciones de la Garantía
                </label>
                <textarea
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24"
                  placeholder="Describe el problema o detalles de la garantía..."
                />
              </div>

              {/* WhatsApp */}
              <div className="border-t pt-4">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="enviarWP"
                    checked={enviarWhatsApp}
                    onChange={(e) => setEnviarWhatsApp(e.target.checked)}
                    className="w-4 h-4 text-blue-600 cursor-pointer"
                  />
                  <label htmlFor="enviarWP" className="ml-2 text-sm font-medium text-gray-700 cursor-pointer">
                    Enviar notificación por WhatsApp al proveedor/laboratorio
                  </label>
                </div>

                {enviarWhatsApp && (
                  <input
                    type="tel"
                    value={numeroProveedor}
                    onChange={(e) => setNumeroProveedor(e.target.value)}
                    placeholder="Ej: +5491123456789"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                )}
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 transition flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-5 h-5" />
                      Guardar Garantía
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            // Historial de garantías
            <div className="space-y-4">
              {garantias.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-lg font-medium">No hay garantías registradas</p>
                  <p className="text-sm">Crea una nueva garantía en la pestaña anterior</p>
                </div>
              ) : (
                garantias.map((garantia) => (
                  <div key={garantia.id} className="border border-gray-300 rounded-lg p-4 hover:shadow-md transition">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-800">
                            {garantia.tipo_garantia === 'cristal' ? '💎' : '👓'} {garantia.descripcion_producto}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getEstadoColor(garantia.estado)}`}>
                            {getEstadoLabel(garantia.estado)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          📅 {new Date(garantia.fecha_inicio).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {garantia.observaciones && (
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Observaciones:</strong> {garantia.observaciones}
                      </p>
                    )}

                    {garantia.numero_proveedor && (
                      <p className="text-sm text-gray-600 mb-3">
                        <strong>Proveedor:</strong> {garantia.numero_proveedor}
                      </p>
                    )}

                    {garantia.mensaje_enviado && (
                      <p className="text-sm text-green-700 mb-3 flex items-center gap-1">
                        <CheckIcon className="w-4 h-4" />
                        WhatsApp enviado: {new Date(garantia.fecha_envio_mensaje).toLocaleString()}
                      </p>
                    )}

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleActualizarEstado(garantia)}
                        className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition"
                      >
                        Cambiar Estado
                      </button>
                      {garantia.numero_proveedor && (
                        <button
                          onClick={() => handleEnviarWhatsApp(garantia)}
                          className="flex-1 px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition flex items-center justify-center gap-1"
                        >
                          <PaperAirplaneIcon className="w-4 h-4" />
                          WhatsApp
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalGestionGarantia;
